import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Loader2,
  MessageSquare,
  PencilLine,
  X,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useConversationPanel } from '../../hooks/useConversationPanel';
import { getErrorMessage } from '../../lib/apiClient';
import { fileKeys, getContents, getFile } from '../files/filesApi';
import ConversationSidebar from '../files/components/ConversationSidebar';
import { editorKeys, fileOpenPath, getEditorConfig } from './editorApi';
import OnlyOfficeEditor from './OnlyOfficeEditor';

/**
 * Full-screen ONLYOFFICE editor at /files/:fileId/edit — a chrome-less takeover
 * mirroring FileViewerPage. Requests edit mode; the backend downgrades to view
 * when the caller lacks write access or the type isn't round-trippable, and this
 * page reflects that in the header. Close returns to where the user came from
 * (router state `from`, set by the files browser) — falling back to the file
 * viewer for viewer-launched sessions and deep links.
 */
export default function EditorPage() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'view' ? 'view' : 'edit';

  // saveStatus: 'saved' (no pending changes) | 'editing' (unsaved changes)
  const [saveStatus, setSaveStatus] = useState('saved');
  const [editorError, setEditorError] = useState(null);
  const [conversationOpen, setConversationOpen] = useConversationPanel();

  // Sibling navigation keeps this component mounted while :fileId changes —
  // don't let the previous document's status/error bleed into the next one.
  // (conversationOpen intentionally survives, matching the file viewer.)
  useEffect(() => {
    setSaveStatus('saved');
    setEditorError(null);
  }, [fileId]);

  // File record (folderId for sibling nav) + the comment sidebar's subject.
  const { data: file } = useQuery({
    queryKey: fileKeys.file(fileId),
    queryFn: () => getFile(fileId),
    retry: false,
  });

  // Sibling files in the same folder (same order and cache key as the file
  // viewer), for previous/next navigation without leaving the editor context.
  const { data: siblingsData } = useQuery({
    queryKey: fileKeys.contents(file?.folderId ?? null, { viewerSiblings: true }),
    queryFn: () =>
      getContents(file?.folderId, { page: 1, limit: 100, sortBy: 'name', sortOrder: 'ASC' }),
    enabled: Boolean(file),
    staleTime: 30_000,
  });
  const siblings = siblingsData?.files?.data ?? [];
  const siblingIndex = siblings.findIndex((f) => f.id === fileId);
  const prevFile = siblingIndex > 0 ? siblings[siblingIndex - 1] : null;
  const nextFile =
    siblingIndex >= 0 && siblingIndex < siblings.length - 1 ? siblings[siblingIndex + 1] : null;

  const { data, isLoading, error } = useQuery({
    queryKey: editorKeys.config(fileId, mode),
    queryFn: () => getEditorConfig(fileId, mode),
    // 409 = the previous session is finishing its final save; retry briefly
    // until it closes and a fresh document key is minted. Don't retry anything else.
    retry: (count, err) => count < 3 && err?.response?.status === 409,
    retryDelay: (count) => Math.min(1000 * 2 ** count, 4000),
    refetchOnWindowFocus: false,
    // Shorter than the backend editor-token TTL so a remount refetches a fresh
    // signed token rather than reusing a possibly-expired cached one.
    staleTime: 2 * 60 * 1000,
  });

  const backPath = location.state?.from ?? `/files/${fileId}`;
  const close = () => navigate(backPath);

  // Office siblings stay in the editor, natively previewable ones open the
  // viewer; the origin (`from`) rides along so close keeps returning there.
  const goToSibling = (sibling) =>
    navigate(fileOpenPath(sibling), { state: { from: location.state?.from } });

  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack Escape while editing (the editor uses it); only when idle.
      if (e.key === 'Escape' && saveStatus === 'saved') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveStatus, fileId]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Spinner label="Preparing editor…" />
      </div>
    );
  }

  if (error || !data?.config) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-slate-400" />
          <h1 className="text-lg font-semibold text-slate-800">This document can’t be opened</h1>
          <p className="text-sm text-slate-500">
            {getErrorMessage(error, 'The editor is unavailable for this file type.')}
          </p>
          <Button onClick={() => navigate(backPath)}>Back</Button>
        </div>
      </div>
    );
  }

  const { documentServerUrl, fileName, canEdit, config } = data;

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <header className="relative flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
        {/* Previous/next — centered in the toolbar, mirroring FileViewerPage */}
        {siblings.length > 1 && siblingIndex >= 0 && (
          <div className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 sm:flex">
            <button
              onClick={() => goToSibling(prevFile)}
              disabled={!prevFile}
              title="Previous file"
              aria-label="Previous file"
              className="rounded-lg border border-slate-200 p-2 text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-max text-xs tabular-nums text-slate-400">
              {siblingIndex + 1} / {siblings.length}
            </span>
            <button
              onClick={() => goToSibling(nextFile)}
              disabled={!nextFile}
              title="Next file"
              aria-label="Next file"
              className="rounded-lg border border-slate-200 p-2 text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        <button
          onClick={close}
          aria-label="Back"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <FileText className="h-5 w-5 shrink-0 text-slate-400" />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{fileName}</p>

        {canEdit ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"
            aria-live="polite"
          >
            {saveStatus === 'editing' ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Autosaving…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                All changes saved
              </>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-200 px-2 py-1 text-xs font-medium text-slate-600">
            <Eye className="h-3.5 w-3.5" />
            View only
          </span>
        )}

        {canEdit && (
          <span className="hidden items-center gap-1.5 text-xs text-slate-400 sm:inline-flex">
            <PencilLine className="h-3.5 w-3.5" />
            Editing
          </span>
        )}

        {file && (
          <button
            onClick={() => setConversationOpen((o) => !o)}
            aria-label="Toggle comments and chat"
            aria-pressed={conversationOpen}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium transition-colors ${
              conversationOpen ? 'bg-slate-200 text-slate-800' : 'text-slate-700 hover:bg-slate-200'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Comments</span>
          </button>
        )}
        <button
          onClick={close}
          aria-label="Close editor"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {editorError && (
        <div className="shrink-0 bg-rose-50 px-4 py-2 text-center text-sm text-rose-700">
          {typeof editorError === 'string' ? editorError : 'The editor reported an error.'}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <main className="min-h-0 flex-1">
          <OnlyOfficeEditor
            documentServerUrl={documentServerUrl}
            config={config}
            onStateChange={(hasChanges) => setSaveStatus(hasChanges ? 'editing' : 'saved')}
            onError={(msg) => setEditorError(msg)}
          />
        </main>
        {conversationOpen && file && <ConversationSidebar file={file} />}
      </div>
    </div>
  );
}
